import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useEffect, useRef } from 'react';

import {
  NumberInput,
  NumberInputGroup,
  NumberInputInput,
  NumberInputTrigger,
  type NumberInputInputRef,
} from '@/components/Form';
import { FieldLabel } from '@/components/Form/fields';
import type { FilterOperatorRange, FilterOperatorValue } from '@/components/InstantSearch';
import { Link, Text } from '@/components/typography';
import { cn } from '@/lib/utils';

type Props = {
  type?: 'number' | 'percent';
  value?: FilterOperatorRange;
  status?: 'error' | 'warning' | undefined;
  compact?: boolean;
  autoFocus?: boolean;
  onChange?: (value: FilterOperatorValue) => void;
};

const numberFormatOptionsByType = {
  number: { useGrouping: true } satisfies Intl.NumberFormatOptions,
  // Base UI applies `maximumFractionDigits` to the raw decimal value before formatting.
  // For percent fields, 1% is stored as 0.01, so we need at least 2 (and 3 for 0.1% precision)
  // to avoid values rounding down to 0 during commits.
  percent: { style: 'percent', maximumFractionDigits: 3 } satisfies Intl.NumberFormatOptions,
};

const toNullableNumber = (value: string | number | null | undefined): number | null => {
  if (value == null || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const RangeControl = ({
  type = 'number',
  value,
  status: statusProp,
  compact = false,
  autoFocus = false,
  onChange,
}: Props) => {
  const minInputRef = useRef<NumberInputInputRef>(null);
  const min = toNullableNumber(value?.min?.operand);
  const max = toNullableNumber(value?.max?.operand);
  const status = statusProp;

  useEffect(() => {
    if (autoFocus) {
      minInputRef.current?.focus();
    }
  }, [minInputRef, autoFocus]);

  const handleRangeChange = (min: number | null, max: number | null) => {
    if (min == null && max == null) {
      onChange?.(null);
    } else {
      onChange?.({
        min: min == null ? undefined : { inclusive: true, operand: `${min}` },
        max: max == null ? undefined : { inclusive: true, operand: `${max}` },
      });
    }
  };

  const handleMinChange = (newMin: number | null) => {
    handleRangeChange(newMin, max);
  };

  const handleMaxChange = (newMax: number | null) => {
    handleRangeChange(min, newMax);
  };

  const handleClear = () => {
    handleRangeChange(null, null);
  };
  const inputValidationProps =
    status === 'error'
      ? ({ 'aria-invalid': true } as const)
      : status === 'warning'
        ? ({ 'data-warning': '' } as const)
        : {};

  return (
    <div className="flex flex-col items-start gap-2">
      {!compact && (
        <>
          <Link variant="caption1" onClick={handleClear}>
            <Trans>Clear</Trans>
          </Link>
          <FieldLabel variant="medium">
            <Trans>Between</Trans>
          </FieldLabel>
        </>
      )}
      <div className={cn('flex items-center', compact ? 'gap-2' : 'gap-4')}>
        <NumberInput
          className="flex-1"
          value={min}
          min={0}
          max={999_999_999}
          step={type === 'percent' ? 0.01 : 1}
          format={numberFormatOptionsByType[type]}
          onValueChange={handleMinChange}
        >
          <NumberInputGroup>
            <NumberInputInput
              ref={minInputRef}
              aria-label={t`Change minimum value in range`}
              placeholder="min"
              {...inputValidationProps}
            />
            <NumberInputTrigger />
          </NumberInputGroup>
        </NumberInput>
        {compact && (
          <Text variant="caption1">
            <Trans>-</Trans>
          </Text>
        )}
        <NumberInput
          className="flex-1"
          value={max}
          min={0}
          max={999_999_999}
          step={type === 'percent' ? 0.01 : 1}
          format={numberFormatOptionsByType[type]}
          onValueChange={handleMaxChange}
        >
          <NumberInputGroup>
            <NumberInputInput
              aria-label={t`Change maximum value in range`}
              placeholder="max"
              {...inputValidationProps}
            />
            <NumberInputTrigger />
          </NumberInputGroup>
        </NumberInput>
      </div>
    </div>
  );
};

export default RangeControl;
